import { useState } from "react";
import { useBrand } from "../../branding/BrandContext";
import { Amount, ChainBadge, CopyField, Notice } from "../../components/ui";
import { QrCode } from "../../components/QrCode";
import { Tabs } from "../../components/Tabs";
import { useI18n } from "../../i18n";
import type { MessageKey } from "../../i18n/strings";
import { chainInfo } from "../../lib/chains";
import { diffUnits, formatDisplay, formatUnits } from "../../lib/money";
import { safeStorage } from "../../lib/storage";
import type { AssetSymbol, ChainId, PublicInvoice } from "../../lib/types";

export type ExchangeId = "binance" | "okx" | "bybit" | "other";
export const EXCHANGES: ExchangeId[] = ["binance", "okx", "bybit", "other"];
const EXCHANGE_NAMES: Record<Exclude<ExchangeId, "other">, string> = { binance: "Binance", okx: "OKX", bybit: "Bybit" };

/**
 * Step keys per exchange. Binance documents a
 * "receive amount" withdrawal mode; for OKX and Bybit the UI default is unverified, so the copy asks
 * the customer to check the received amount instead of naming a button. No fee figures are shown —
 * they change and differ per network.
 */
export function guideSteps(exchange: ExchangeId): MessageKey[] {
  switch (exchange) {
    case "binance":
      return ["guide.open", "guide.network", "guide.binance.amount", "guide.confirm"];
    case "okx":
      return ["guide.okx.open", "guide.network", "guide.okx.amount", "guide.confirm"];
    case "bybit":
      return ["guide.open", "guide.network", "guide.bybit.amount", "guide.confirm"];
    case "other":
      return ["guide.open", "guide.network", "guide.other.amount", "guide.confirm"];
  }
}

function initialExchange(): ExchangeId {
  const saved = safeStorage.get("exchange");
  return EXCHANGES.includes(saved as ExchangeId) ? (saved as ExchangeId) : "binance";
}

export function ExchangePay({ invoice, walletAvailable = false }: { invoice: PublicInvoice; walletAvailable?: boolean }) {
  return (
    <ExchangeTransfer
      exactAmount={invoice.exchangeAmount}
      baseAmount={invoice.amount}
      decimals={invoice.asset.decimals}
      asset={invoice.asset.symbol}
      chain={invoice.asset.chain}
      address={invoice.merchantAddress}
      walletAvailable={walletAvailable}
    />
  );
}

/**
 * A plain transfer of an exact, unique amount to an address: the exchange tab of the checkout and the
 * fee-credit top-up page. Amounts are base units.
 */
export function ExchangeTransfer({
  exactAmount,
  baseAmount,
  decimals,
  asset,
  chain: chainId,
  address,
  walletAvailable = false,
  waitingTitle,
}: {
  exactAmount: string;
  baseAmount: string;
  decimals: number;
  asset: AssetSymbol;
  chain: ChainId;
  address: string;
  walletAvailable?: boolean;
  waitingTitle?: string;
}) {
  const { t, locale } = useI18n();
  const brand = useBrand();
  const instructions = brand.checkout.showExchangeInstructions;
  const [exchange, setExchange] = useState<ExchangeId>(initialExchange);
  const chain = chainInfo(chainId);
  // Exact value to paste: "." decimal, no grouping, every significant digit.
  const exact = formatUnits(exactAmount, decimals);
  const base = formatUnits(baseAmount, decimals);
  const extra = diffUnits(exactAmount, baseAmount);
  const baseFractionLength = base.split(".")[1]?.length ?? 0;
  const displayExact = formatDisplay(exactAmount, decimals, locale);
  const network = chain.exchangeNetwork;
  const pick = (id: ExchangeId) => {
    setExchange(id);
    safeStorage.set("exchange", id);
  };

  return (
    <div className="exchange">
      <Notice tone="warning" title={t("exchange.warningTitle", { amount: displayExact, asset })}>
        {t("exchange.warningBody", { network: chain.name })}
      </Notice>

      <div className={`exchange-grid${instructions ? "" : " exchange-grid-single"}`}>
        <div className="exchange-fields">
          <CopyField
            label={t("exchange.amount")}
            value={exact}
            display={
              <span className="exchange-amount">
                <Amount value={exact} emphasiseFrom={extra !== "0" ? baseFractionLength : undefined} />
                <span className="exchange-asset">{asset}</span>
              </span>
            }
            hint={extra !== "0" ? t("exchange.amountHint", { extra: formatDisplay(extra, decimals, locale, 0), asset }) : undefined}
          />

          <div className="copy-field">
            <div className="field-label">{t("exchange.network")}</div>
            <div className="network-row">
              <ChainBadge chain={chainId} />
            </div>
          </div>

          <CopyField
            label={t("exchange.address", { asset, chain: chain.name })}
            value={address}
            display={<AddressText value={address} />}
            mono
          />
        </div>

        {instructions ? (
          <div className="exchange-qr">
            <QrCode value={address} label={t("exchange.address", { asset, chain: chain.name })} />
            <span className="muted small">{t("exchange.scan")}</span>
          </div>
        ) : null}
      </div>

      <div className="waiting" role="status">
        <span className="pulse" aria-hidden="true" />
        <div>
          <div className="waiting-title">{waitingTitle ?? t("exchange.waiting")}</div>
          <div className="muted small">{t("exchange.waitingHint")}</div>
        </div>
      </div>

      {instructions ? (
        <section className="exchange-steps" aria-labelledby="guide-title">
          <h3 className="field-label" id="guide-title">
            {t("guide.title")}
          </h3>
          <Tabs<ExchangeId>
            idBase="guide"
            label={t("guide.pick")}
            active={exchange}
            onChange={pick}
            size="sm"
            tabs={EXCHANGES.map((id) => ({ key: id, label: id === "other" ? t("guide.other") : EXCHANGE_NAMES[id] }))}
          />
          <ol role="tabpanel" id={`guide-panel-${exchange}`} aria-labelledby={`guide-tab-${exchange}`} tabIndex={0}>
            {guideSteps(exchange).map((key) => (
              <li key={key}>{t(key, { asset, network, amount: exact })}</li>
            ))}
          </ol>
        </section>
      ) : null}

      <div className="exchange-notes">
        <Notice tone="info" title={t("exchange.internalTitle")}>
          {t("exchange.internalBody")}
        </Notice>
        <p className="muted small">
          {t("exchange.minimumBody", { amount: displayExact, asset })}
          {walletAvailable ? ` ${t("exchange.minimumWallet")}` : ""}
        </p>
        {!instructions ? <p className="muted small">{t("exchange.feeWarning", { amount: displayExact, asset })}</p> : null}
      </div>
    </div>
  );
}

/**
 * A long address with a soft break in the middle: one line where it fits, two even halves in a
 * narrow column (instead of a lone trailing character). The text itself is unchanged for copying.
 */
export function AddressText({ value }: { value: string }) {
  const mid = Math.ceil(value.length / 2);
  return (
    <span className="address-break">
      {value.slice(0, mid)}
      <wbr />
      {value.slice(mid)}
    </span>
  );
}
